import { DropDown } from '@components/core-ui/form-elements/DropDown';
import Dots from '@components/icons/Dots.svg';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { formatDateMsDsY } from '@helpers/formatDateMsDsY';
import { formatRate } from '@helpers/formatRate';
import { getHourPrice } from '@helpers/getHourPrice';
import { isSubscriptionEditable } from '@helpers/isSubscriptionEditable';
import { useModalContext } from '@providers/ModalProvider';
import { GetAllSubscriptionPlansPlanResponseDto, GetBillingAccountResponseSubscriptionDto } from 'app/API';
import { useState } from 'react';
import { DotsButton } from './styles/DotsButton';
import { Wrapper } from './styles/Wrapper';

type TProps = {
  className?: string;
  reload: () => void;
  plan: GetAllSubscriptionPlansPlanResponseDto;
  subscription: GetBillingAccountResponseSubscriptionDto;
};

export const ActiveCard = ({ className = '', plan, subscription, reload }: TProps) => {
  const { openModal, closeModal } = useModalContext();
  // const licensesCount = subscription?.licenses?.length || 1;

  const [showDropDown, setShowDropDown] = useState(false);

  const planName = plan?.name.replace('AutoScriptOne ', '').replace('RealTime ', '');
  const price = getHourPrice(Number(plan?.price), true);
  const billingConfiguration = plan.DefaultBillingConfiguration;

  const unassignedCount = subscription?.licenses.filter((lic) => lic.assigneeId === null).length;
  const assignedCount = subscription?.licenses.filter((lic) => lic.assigneeId !== null).length;

  if (!billingConfiguration) {
    return null;
  }

  const dropDownOptions: {
    title: string;
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  }[] = [];

  if (isSubscriptionEditable(subscription.status)) {
    dropDownOptions.push({
      title: 'Edit Subscription',
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        openModal(ModalNamesEnum.AdminEditASOneSubscriptionModal, {
          subscription,
          onSuccess: () => {
            reload();
            closeModal();
          },
        });
      },
    });
  }
  dropDownOptions.push({
    title: 'Cancel',
    onClick: (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      openModal(ModalNamesEnum.AdminConfirmSubscriptionCancellation, {
        subscriptionId: subscription.id,
        planName: plan.name,
        onSuccess: () => {
          reload();
          closeModal();
        },
      });
    },
  });

  const renderOptions = () => {
    return (
      <>
        <DotsButton
          onClick={(e) => {
            e.stopPropagation();
            setShowDropDown(true);
          }}
          data-testid="subscription-dropdown"
        >
          <Dots />
        </DotsButton>
        {showDropDown ? <DropDown close={() => setShowDropDown(false)} options={dropDownOptions} /> : null}
      </>
    );
  };

  return (
    <Wrapper className={className}>
      <div className="content">
        <h2 className="title">AutoScriptOne</h2>
        <h2 className="plan-name">{planName}</h2>
        {dropDownOptions.length > 0 && renderOptions()}

        <div className="price flex gap-3 pt-1 pb-2">
          <div>
            <div>Monthly</div>
            <div>{price}/seat</div>
          </div>
          <div className="divider"></div>
          <div>
            <div>Active</div>
            <div>
              {assignedCount}/{assignedCount + unassignedCount}
            </div>
          </div>
        </div>

        {billingConfiguration.postProcessingRateType !== 'DISALLOWED' &&
        billingConfiguration.postProcessingRateType !== 'TIER_RATE' ? (
          <h3>
            {formatRate(
              billingConfiguration.postProcessingRate,
              billingConfiguration.postProcessingRateType,
              'Post ASR',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.realTimeRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.realTimeRate,
              billingConfiguration.realTimeRateType,
              'Real-time streaming',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.partialUnlockRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.partialUnlockRate,
              billingConfiguration.partialUnlockRateType,
              'Reporter unlock',
              false,
            )}
          </h3>
        ) : null}
        {billingConfiguration.fullUnlockRateType !== 'DISALLOWED' ? (
          <h3>
            {formatRate(
              billingConfiguration.fullUnlockRate,
              billingConfiguration.fullUnlockRateType,
              'Full unlock',
              false,
            )}
          </h3>
        ) : null}
        {subscription.currentPeriodEnd ? <h3>Next payment: {formatDateMsDsY(subscription.currentPeriodEnd)}</h3> : null}
        {subscription.freeTrialEndDate ? (
          <h3>Trial expiration: {formatDateMsDsY(subscription.freeTrialEndDate)}</h3>
        ) : null}
      </div>
    </Wrapper>
  );
};
